import React from 'react'
import { useAuth } from '@/modules/auth/context/AuthContext'
import { ensureRenewed, getExistingSubscription, isPwaInstalled, subscribePush, unsubscribePush, ensureSwActiveAndControlled, getResetEpoch } from '../services/webpushClient'
import { supabase } from '@/lib/supabase'

export function usePushSubscription() {
  const { user, profile } = useAuth()
  const [status, setStatus] = React.useState<'idle' | 'subscribed' | 'unsubscribed' | 'blocked' | 'unsupported'>('idle')
  const [loading, setLoading] = React.useState(true)
  const [resetEpoch, setResetEpoch] = React.useState<number | null>(null)
  const [forceReoptin, setForceReoptin] = React.useState<boolean>(false)
  const [shouldPrompt, setShouldPrompt] = React.useState<boolean>(false)

  function getOptOut(): boolean {
    try { return localStorage.getItem('webpush.optout') === '1' } catch { return false }
  }
  function clearOptPrefs() {
    try { localStorage.removeItem('webpush.optout'); localStorage.removeItem('webpush.optin.snooze_until') } catch {}
  }

  React.useEffect(() => {
    let mounted = true
    ;(async () => {
      try {
        if (!user?.id) { setStatus('unsupported'); return }
        // reset global força reopt-in
        try {
          const re = await getResetEpoch()
          if (mounted) {
            setResetEpoch(re)
            const ack = Number(localStorage.getItem('webpush.reset.ack') || '')
            if (re && (!Number.isFinite(ack) || ack < re)) setForceReoptin(true)
          }
        } catch {}
        // não renovar automaticamente se optou por desligar
        if (getOptOut()) { setStatus('unsubscribed'); return }
        // se reset ativo, não reativa automaticamente
        if (forceReoptin) { setStatus('unsubscribed'); return }
        const s = await ensureRenewed(user.id, profile?.role || null)
        if (mounted) setStatus(s)
      } finally { if (mounted) setLoading(false) }
    })()
    return () => { mounted = false }
  }, [user?.id, profile?.role, forceReoptin])

  // Realtime: se a inscrição do usuário for desativada (active=false) ou removida, solicitar reativação
  React.useEffect(() => {
    if (!user?.id) return
    const ch = supabase.channel(`realtime:webpush_subs:${user.id}`)
      .on('postgres_changes', {
        event: '*',
        schema: 'public',
        table: 'webpush_subscriptions',
        filter: `user_id=eq.${user.id}`,
      }, (payload: any) => {
        try {
          if (payload.eventType === 'DELETE') {
            setShouldPrompt(true)
            setStatus('unsubscribed')
            return
          }
          const row = payload.new || payload.record || null
          if (row && row.active === false) {
            setShouldPrompt(true)
            setStatus('unsubscribed')
          }
        } catch {}
      })
      .subscribe()
    return () => { try { supabase.removeChannel(ch) } catch {} }
  }, [user?.id])

  async function enable() {
    if (!user?.id) return false
    // iOS/Safari requer gesto explícito do usuário
    try {
      if (typeof Notification !== 'undefined' && Notification.permission !== 'granted') {
        const p = await Notification.requestPermission()
        if (p !== 'granted') return false
      }
      // Garante SW ativo e controlando a página (iOS pós-instalação)
      const okSw = await ensureSwActiveAndControlled()
      if (okSw === 'reload_needed') {
        console.warn('[push] SW não controla a página: recarregar recomendado')
        return false
      }
      const sub = await subscribePush(user.id, profile?.role || null)
      setStatus(sub ? 'subscribed' : 'unsubscribed')
      if (sub) {
        clearOptPrefs()
        try {
          const ackVal = resetEpoch && Number.isFinite(resetEpoch) ? String(resetEpoch) : String(Date.now())
          localStorage.setItem('webpush.reset.ack', ackVal)
        } catch {}
      }
      return !!sub
    } catch (e) {
      console.error('[push] enable error', e)
      setStatus('unsubscribed')
      return false
    }
  }

  async function disable() {
    await unsubscribePush(user?.id)
    setStatus('unsubscribed')
    try { localStorage.setItem('webpush.optout', '1'); localStorage.removeItem('webpush.optin.snooze_until') } catch {}
    return true
  }

  return {
    user,
    profile,
    status,
    loading,
    pwaInstalled: isPwaInstalled(),
    getExistingSubscription,
    enable,
    disable,
    getOptOut,
    resetEpoch,
    forceReoptin,
    shouldPrompt,
  }
}
